import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Plus, Trash2, Pencil, Refrigerator, Snowflake, Archive, ArchiveRestore, Package, ArrowDownToLine, History, Settings, SprayCan, Bath, ChevronDown, Sparkles, AlertTriangle, Clock, ShoppingCart, PackageCheck, PackageX, X, MoreVertical, Search, Receipt, Check, Camera, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import PantrySubTabs from "@/components/PantrySubTabs";
import PantryTrashDialog from "@/components/pantry/PantryTrashDialog";
import DeleteAllPantryDialog from "@/components/pantry/DeleteAllPantryDialog";
import QuickAddPantryDialog from "@/components/pantry/QuickAddPantryDialog";
import DuplicateBanner from "@/components/DuplicateBanner";
import EditCategoryGroupsDialog from "@/components/EditCategoryGroupsDialog";
import BarcodeScannerDialog from "@/components/BarcodeScannerDialog";
import ItemImagePicker from "@/components/ItemImagePicker";
import PantryCleanupDialog from "@/components/PantryCleanupDialog";
import VerifyDot from "@/components/pantry/VerifyDot";
import AgeStripe from "@/components/pantry/AgeStripe";
import CountingMode from "@/components/pantry/CountingMode";
import { usePantryCleanup } from "@/lib/usePantryCleanup";

const TYPE_META = {
  fridge: { icon: Refrigerator, color: "bg-sky-50 text-sky-700 border-sky-200", label: "Kjøleskap" },
  freezer: { icon: Snowflake, color: "bg-indigo-50 text-indigo-700 border-indigo-200", label: "Fryser" },
  dry: { icon: Archive, color: "bg-amber-50 text-amber-700 border-amber-200", label: "Tørrproviant" },
  cleaning: { icon: SprayCan, color: "bg-sailcloth-100 text-navy-800 border-navy-100", label: "Renhold" },
  bathroom: { icon: Bath, color: "bg-purple-50 text-purple-700 border-purple-200", label: "Toalett & Bad" },
};

const CATEGORIES = [
  // Matgrupper (kanoniske norske)
  "Frukt", "Grønnsaker", "Meieri", "Egg", "Kjøtt", "Fisk", "Grillmat", "Pålegg",
  "Bakevarer", "Mel & Bake", "Pasta & Ris", "Hermetikk", "Saus & Dressing",
  "Krydder", "Snacks", "Drikke", "Frysevarer", "Renhold", "Bad",
  "Annet",
];
// Legacy → kanonisk norsk kategori (unngår duplikate seksjoner)
const LEGACY_TO_CANONICAL = {
  Produce: "Frukt", // nærmeste (Frukt/Grønt) – default Frukt; "Grønt" mangler i legacy
  Dairy: "Meieri",
  Meat: "Kjøtt",
  Fish: "Fisk",
  Bakery: "Bakevarer",
  Pantry: "Pasta & Ris",
  Frozen: "Frysevarer",
  Spices: "Krydder",
  Cleaning: "Renhold",
  Bathroom: "Bad",
  Other: "Annet",
  "Kjølevare": "Meieri",
  "Frysevare": "Frysevarer",
  "Tørrvare": "Pasta & Ris",
  // Migrer eksisterande "Kjøtt & Fisk" til "Kjøtt" som default (brukar kan
  // flytte spesifikke fiskvarer til Fisk-fanen manuelt)
  "Kjøtt & Fisk": "Kjøtt",
};
const CATEGORY_LABELS = {
  // Matgrupper – samme navn for visning
  Frukt: "Frukt", "Grønnsaker": "Grønnsaker", Meieri: "Meieri", Egg: "Egg",
  "Kjøtt": "Kjøtt", "Fisk": "Fisk", "Grillmat": "Grillmat", "Pålegg": "Pålegg", Bakevarer: "Bakevarer",
  "Mel & Bake": "Mel & Bake", "Pasta & Ris": "Pasta & Ris", Hermetikk: "Hermetikk",
  "Saus & Dressing": "Saus & Dressing", Krydder: "Krydder", Snacks: "Snacks",
  Drikke: "Drikke", "Frysevarer": "Frysevarer", Renhold: "Renhold", Bad: "Bad & Toalett",
  Annet: "Annet",
};

const UNITS = ["stk", "kg", "g", "l", "dl", "ss", "ts", "pk", "boks", "pose", "fl"];

const emptyItem = { name: "", amount: "", unit: "stk", category: "Other", location_id: "", expiry_date: "", note: "", image_url: "" };

export default function PantryPage() {
  const { activeBoat, loadActiveBoat } = useAuth();
  const isHousehold = activeBoat?.kind === "household";
  const stockLabel = isHousehold ? "hjemme" : "om bord";
  const stockLabelTitle = isHousehold ? "Hjemme" : "Om bord";
  const [searchQuery, setSearchQuery] = useState("");
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [activeLocId, setActiveLocId] = useState("all");
  const [activeCategory, setActiveCategory] = useState("all");
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [pantrySort, setPantrySort] = useState("default"); // "default" | "alpha"
  const [searchParams, setSearchParams] = useSearchParams();
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyItem });
  const [locDialogOpen, setLocDialogOpen] = useState(false);
  const [newLoc, setNewLoc] = useState({ name: "", type: "fridge" });
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawItem, setWithdrawItem] = useState(null);
  const [withdrawForm, setWithdrawForm] = useState({ amount: "", reason: "" });
  const [showHistory, setShowHistory] = useState(false);
  const [editingLocId, setEditingLocId] = useState(null);
  const [deletePinOpen, setDeletePinOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [standardizeOpen, setStandardizeOpen] = useState(false);
  const [standardizeChanges, setStandardizeChanges] = useState([]);
  const [standardizeLoading, setStandardizeLoading] = useState(false);
  const moreMenuRef = useRef(null);

  // ─── "Sist opptelt" / verification mode ───
  const [countingOpen, setCountingOpen] = useState(false);
  const [verifyingIds, setVerifyingIds] = useState(new Set());
  const [verificationThreshold, setVerificationThreshold] = useState(14);
  const [verificationStatus, setVerificationStatus] = useState({ total: 0, overdue_count: 0, never_verified_count: 0 });

  const handleVerifyItem = async (itemId) => {
    if (verifyingIds.has(itemId)) return;
    setVerifyingIds((s) => new Set([...s, itemId]));
    try {
      const res = await api.verifyPantryItem(itemId);
      const updated = res.data;
      setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, last_verified_at: updated.last_verified_at } : i)));
      toast.success(`«${updated.name}» bekreftet opptelt`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikkje bekrefte");
    } finally {
      setVerifyingIds((s) => { const n = new Set(s); n.delete(itemId); return n; });
    }
  };

  const handleVerifyCategory = async (category, catItems) => {
    if (!catItems || catItems.length === 0) return;
    if (!window.confirm(`Bekreft alle ${catItems.length} varer i ${category} som opptalt nå?`)) return;
    try {
      const ids = catItems.map((i) => i.id);
      const res = await api.bulkVerifyPantry({ item_ids: ids });
      const today = new Date().toISOString().slice(0, 10);
      setItems((prev) => prev.map((i) => (ids.includes(i.id) ? { ...i, last_verified_at: today } : i)));
      toast.success(`${res.data.verified || ids.length} varer bekreftet`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikkje bekrefte gruppe");
    }
  };

  // Close "more" menu on outside click
  useEffect(() => {
    if (!moreMenuOpen) return;
    const onDoc = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setMoreMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [moreMenuOpen]);
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [chipDraggedIdx, setChipDraggedIdx] = useState(null);
  const [chipDragOverIdx, setChipDragOverIdx] = useState(null);
  const [editGroupsOpen, setEditGroupsOpen] = useState(false);
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const [imageEditItem, setImageEditItem] = useState(null); // pantry item whose image is being edited via popup
  const [imageEditUrl, setImageEditUrl] = useState("");
  const [imageEditSaving, setImageEditSaving] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [autoFixImagesOpen, setAutoFixImagesOpen] = useState(false);
  const cleanup = usePantryCleanup({ onChanged: () => loadData() });
  // Autocomplete state
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);

  const DELETE_ALL_PIN = activeBoat?.delete_code || "1234";

  const handleRemoveEmpty = async () => {
    if (!window.confirm("Fjern alle varer med mengde 0? De flyttes til papirkurven og kan gjenopprettes.")) return;
    try {
      const res = await api.removeEmptyPantryItems();
      const n = res.data?.removed || 0;
      if (n === 0) toast.info("Ingen tomme varer funnet");
      else toast.success(`${n} tomme varer flyttet til papirkurv`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved opprydding");
    }
  };

  const openStandardizeDialog = async () => {
    setStandardizeOpen(true);
    setStandardizeLoading(true);
    setStandardizeChanges([]);
    try {
      const res = await api.standardizePantryNames(true);
      setStandardizeChanges(res.data?.changes || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke hente forslag");
    } finally {
      setStandardizeLoading(false);
    }
  };

  const applyStandardize = async () => {
    setStandardizeLoading(true);
    try {
      // Send the (potentially user-edited) names to the new apply-renames
      // endpoint so any in-dialog typo corrections are persisted.
      const renames = standardizeChanges
        .map((c) => ({ id: c.id, name: (c.after || "").trim(), brand: c.brand || "" }))
        .filter((r) => r.id && r.name);
      const res = await api.applyPantryRenames(renames);
      const applied = res.data?.applied || 0;
      const merged = res.data?.merged_groups || 0;
      const deleted = res.data?.deleted_items || 0;
      if (merged > 0) {
        toast.success(`${applied} varenavn oppdatert · ${merged} grupper slått sammen (${deleted} duplikater fjernet)`);
      } else {
        toast.success(`${applied} varenavn oppdatert`);
      }
      setStandardizeOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved oppdatering");
    } finally {
      setStandardizeLoading(false);
    }
  };

  const handleAutoSort = async () => {
    try {
      toast.loading("AI kategoriserer varene...", { id: "auto-sort" });
      const res = await api.autoSortPantry();
      const { total, moved } = res.data;
      if (moved === 0) toast.success(`Alle varer har allerede riktig kategori (${total} varer)`, { id: "auto-sort" });
      else toast.success(`${moved} av ${total} varer fikk oppdatert kategori`, { id: "auto-sort" });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil ved sortering", { id: "auto-sort" });
    }
  };

  // One-click cleanup: strip merker frå alle varenamn + slå saman like varer
  // (deterministisk + AI-kryss-merke). Viser bekreftelse før noko utførast.
  const [oneClickCleanLoading, setOneClickCleanLoading] = useState(false);
  const [cleanupBadgeCount, setCleanupBadgeCount] = useState(0);

  const refreshCleanupBadge = useCallback(async () => {
    try {
      const [stdRes, dupRes] = await Promise.allSettled([
        api.standardizePantryNames(true),
        api.getPantryDuplicates(),
      ]);
      const renames = stdRes.status === "fulfilled" ? (stdRes.value?.data?.changes?.length || 0) : 0;
      const dupGroups = dupRes.status === "fulfilled" ? (dupRes.value?.data?.groups?.length || 0) : 0;
      setCleanupBadgeCount(renames + dupGroups);
    } catch (err) { console.debug("cleanup badge fetch failed", err?.message); }
  }, []);
  const handleOneClickClean = async () => {
    if (oneClickCleanLoading) return;
    // 1) Hent dry-run for å vise brukar kva som vil skje
    let preview = { changes: [] };
    try {
      const r = await api.standardizePantryNames(true);
      preview = r.data || preview;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Klarte ikkje å hente forhåndsvisning");
      return;
    }
    const renameLines = (preview.changes || []).slice(0, 10).map((c) => `• ${c.before} → ${c.after}`);
    const extraRenames = Math.max(0, (preview.changes || []).length - 10);
    const renameSummary = renameLines.length > 0
      ? `${preview.changes.length} varenavn vil bli oppdatert:\n${renameLines.join("\n")}${extraRenames ? `\n…og ${extraRenames} til` : ""}`
      : "Ingen varenavn har butikk-/merke-prefiks å stripe.";
    const msg = `Køyr full opprydding av beholdninga?\n\n${renameSummary}\n\nDeretter blir like varer slått saman automatisk (antall summerast når enheten er lik).`;
    if (!window.confirm(msg)) return;

    setOneClickCleanLoading(true);
    toast.loading("Ryddar opp beholdning…", { id: "pantry-cleanup" });
    try {
      // 2) Apply standardize (strippar merker + auto-merger like namn)
      const stdRes = await api.standardizePantryNames(false);
      const renamed = stdRes.data?.applied || 0;
      const mergedStd = stdRes.data?.merged_groups || 0;
      const deletedStd = stdRes.data?.deleted_items || 0;

      // 3) Køyr deterministisk merge-duplicates på det som er igjen
      let mergedDet = 0;
      let deletedDet = 0;
      try {
        const dup = await api.getPantryDuplicates();
        const keys = ((dup.data?.groups) || []).map((g) => g.key);
        if (keys.length > 0) {
          const mergeRes = await api.mergePantryDuplicates(keys, null);
          mergedDet = mergeRes.data?.merged_groups || 0;
          deletedDet = mergeRes.data?.deleted_items || 0;
        }
      } catch (err) { console.warn("dedup-merge feila", err?.message); }

      const totalMerged = mergedStd + mergedDet;
      const totalDeleted = deletedStd + deletedDet;
      toast.success(
        `Ferdig! ${renamed} varenavn rydda · ${totalMerged} grupper slått saman · ${totalDeleted} duplikater fjerna`,
        { id: "pantry-cleanup", duration: 8000 },
      );
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil under opprydding", { id: "pantry-cleanup" });
    } finally {
      setOneClickCleanLoading(false);
    }
  };

  const loadData = useCallback(async () => {
    try {
      // Always load ALL pantry items (no location filter on the server).
      // Location filtering is applied client-side below so that the search
      // box matches across every location, not just the active tab.
      const [locsRes, itemsRes, wRes, vstatusRes] = await Promise.all([
        api.getStorageLocations(),
        api.getPantry(),
        api.getWithdrawals(),
        api.getPantryVerificationStatus().catch(() => ({ data: { threshold_days: 14 } })),
      ]);
      setLocations(locsRes.data);
      setItems(itemsRes.data);
      setWithdrawals(wRes.data);
      setVerificationThreshold(vstatusRes.data?.threshold_days || 14);
      setVerificationStatus({
        total: vstatusRes.data?.total || 0,
        overdue_count: vstatusRes.data?.overdue_count || 0,
        never_verified_count: vstatusRes.data?.never_verified_count || 0,
      });
      window.dispatchEvent(new Event("pantry-dupe-refresh"));
      // Refresh oransje badge på ⋮-menyen (best-effort)
      refreshCleanupBadge();
    } catch { toast.error("Kunne ikke laste beholdning"); }
  }, [refreshCleanupBadge]);

  useEffect(() => { loadData(); }, [loadData]);

  // Reload data etter at pending mutasjonar er synka
  useEffect(() => {
    const h = () => loadData();
    window.addEventListener("pb-synced", h);
    return () => window.removeEventListener("pb-synced", h);
  }, [loadData]);

  const getLocName = (locId) => locations.find((l) => l.id === locId)?.name || "";
  const getLocType = (locId) => locations.find((l) => l.id === locId)?.type || "fridge";
  const getLocMeta = (locId) => TYPE_META[getLocType(locId)] || TYPE_META.fridge;

  // Derive entries from an item (fallback to legacy flat fields as a single entry)
  const entriesOf = (item) => {
    if (item.locations && item.locations.length > 0) return item.locations;
    return [{
      id: `legacy-${item.id}`,
      location_id: item.location_id || "",
      amount: item.amount || "",
      unit: item.unit || "",
      expiry_date: item.expiry_date || "",
      in_stock: item.in_stock !== false,
      note: item.note || "",
    }];
  };

  // Patch a single location entry in local state optimistically
  const patchEntry = (itemId, entryId, patch) => {
    setItems((prev) => prev.map((i) => {
      if (i.id !== itemId) return i;
      const entries = entriesOf(i).map((e) => (e.id === entryId ? { ...e, ...patch } : e));
      return { ...i, locations: entries };
    }));
  };

  // ─── Item CRUD ───
  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyItem, location_id: activeLocId !== "all" ? activeLocId : (locations[0]?.id || "") });
    setFormOpen(true);
  };

  // Pre-fill the "Add item" dialog from a scanned barcode (+ optional product lookup)
  const handleBarcodeDetected = ({ code, product }) => {
    setEditingId(null);
    const name = (product?.name || "").trim();
    const brand = (product?.brand || "").trim();
    const quantity = (product?.quantity || "").trim();
    // Try to split quantity like "500 g" or "1 l" into amount/unit
    let amount = "";
    let unit = "stk";
    if (quantity) {
      const m = quantity.match(/^([\d,.\s]+)\s*([a-zA-ZøæåØÆÅ]+)/);
      if (m) {
        amount = m[1].replace(",", ".").trim();
        unit = m[2].toLowerCase();
      }
    }
    setForm({
      ...emptyItem,
      name: name || `EAN ${code}`,
      amount: amount || "1",
      unit,
      note: [brand, code ? `EAN: ${code}` : ""].filter(Boolean).join(" • "),
      image_url: (product?.image || "").trim(),
      location_id: activeLocId !== "all" ? activeLocId : (locations[0]?.id || ""),
    });
    setFormOpen(true);
  };

  // Quick "fix image" flow — opens a small dialog with the same ImagePicker so
  // the user can replace / regenerate the photo without entering full edit.
  const openImageEditor = (item) => {
    setImageEditItem(item);
    setImageEditUrl(item.image_url || "");
  };

  const saveImageEdit = async () => {
    if (!imageEditItem) return;
    setImageEditSaving(true);
    try {
      await api.updatePantryItem(imageEditItem.id, { image_url: imageEditUrl });
      toast.success(imageEditUrl ? "Bilde oppdatert" : "Bilde fjernet");
      setImageEditItem(null);
      setImageEditUrl("");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke lagre bilde");
    } finally {
      setImageEditSaving(false);
    }
  };
  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({ name: item.name, amount: item.amount, unit: item.unit, category: item.category, location_id: item.location_id, expiry_date: item.expiry_date || "", note: item.note || "", image_url: item.image_url || "" });
    setFormOpen(true);
  };
  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Navn er påkrevd"); return; }
    try {
      if (editingId) {
        const original = items.find((i) => i.id === editingId);
        const oldName = original?.name || "";
        const res = await api.updatePantryItem(editingId, form);
        setItems((prev) => prev.map((i) => (i.id === editingId ? res.data : i)));
        // If name changed, propagate to shopping/catalog/recipes so other views stay in sync
        if (oldName && oldName.trim() && oldName.trim().toLowerCase() !== form.name.trim().toLowerCase()) {
          try {
            const syncRes = await api.renameItemEverywhere(oldName.trim(), form.name.trim());
            const n = syncRes.data?.updated || 0;
            const bd = syncRes.data?.breakdown || {};
            const parts = [];
            if (bd.shopping_lists > 0) parts.push(`${bd.shopping_lists} handlelister`);
            if (bd.recipes > 0) parts.push(`${bd.recipes} oppskrifter`);
            if (bd.catalog > 0) parts.push(`${bd.catalog} kartotek`);
            if (bd.custom_lists > 0) parts.push(`${bd.custom_lists} lister`);
            toast.success(n > 0 ? `Vare oppdatert – synkronisert ${parts.join(", ") || `${n} steder`}` : "Vare oppdatert");
          } catch { toast.success("Vare oppdatert"); }
        } else {
          toast.success("Vare oppdatert");
        }
      } else {
        const res = await api.createPantryItem(form);
        const newItem = res.data;
        // Optimistic add: prepend to list immediately
        setItems((prev) => [newItem, ...prev.filter((i) => i.id !== newItem.id)]);
        // If item went to a different location than the current tab, switch to "all" so user sees it
        if (activeLocId !== "all" && newItem.location_id !== activeLocId) {
          setActiveLocId("all");
          toast.success(`Vare lagt til i ${getLocName(newItem.location_id) || "beholdningen"}`);
        } else {
          toast.success("Vare lagt til");
        }
      }
      setFormOpen(false); setForm({ ...emptyItem }); setEditingId(null);
      // Background refresh for consistency
      loadData();
    } catch (err) {
      if (err.response?.status === 202) {
        toast.success(err.response?.data?.detail || "Endring sendt til godkjenning");
        setFormOpen(false); setForm({ ...emptyItem }); setEditingId(null);
      } else {
        toast.error(err.response?.data?.detail || "Feil ved lagring");
      }
    }
  };
  const handleDelete = async (id) => {
    try {
      await api.deletePantryItem(id);
      setItems((p) => p.filter((i) => i.id !== id));
      toast.success("Flyttet til papirkurv", {
        action: {
          label: "Angre",
          onClick: async () => {
            try {
              await api.restorePantryTrashItem(id);
              toast.success("Gjenopprettet");
              loadData();
            } catch { toast.error("Kunne ikke gjenopprette"); }
          },
        },
        duration: 6000,
      });
    } catch { toast.error("Feil ved sletting"); }
  };

  const handleLocationChange = async (item, entry, locationId) => {
    if (entry.location_id === locationId) return;
    // Optimistic update
    patchEntry(item.id, entry.id, { location_id: locationId });
    try {
      if (String(entry.id).startsWith("legacy-")) {
        // Legacy doc: falls back to top-level field update
        await api.updatePantryItem(item.id, { location_id: locationId });
      } else {
        await api.updatePantryLocationEntry(item.id, entry.id, { location_id: locationId });
      }
      toast.success("Lagringsplass endret");
      loadData();
    } catch (err) {
      patchEntry(item.id, entry.id, { location_id: entry.location_id });
      if (err.response?.status === 202) toast.success(err.response?.data?.detail || "Sendt til godkjenning");
      else toast.error("Feil ved endring");
    }
  };

  const handleAmountChange = async (item, entry, newAmount) => {
    const cleaned = (newAmount || "").toString().trim();
    if (cleaned === (entry.amount || "").toString()) return;
    patchEntry(item.id, entry.id, { amount: cleaned });
    try {
      if (String(entry.id).startsWith("legacy-")) {
        await api.updatePantryItem(item.id, { amount: cleaned });
      } else {
        await api.updatePantryLocationEntry(item.id, entry.id, { amount: cleaned });
      }
    } catch (err) {
      patchEntry(item.id, entry.id, { amount: entry.amount });
      if (err.response?.status === 202) toast.success(err.response?.data?.detail || "Sendt til godkjenning");
      else toast.error("Kunne ikke oppdatere antall");
    }
  };

  const bumpAmount = (item, entry, delta) => {
    const raw = (entry.amount || "0").toString().replace(",", ".");
    const n = parseFloat(raw);
    const next = isNaN(n) ? (delta > 0 ? "1" : "0") : Math.max(0, n + delta).toString();
    handleAmountChange(item, entry, next);
  };

  const handleUnitChange = async (item, entry, unit) => {
    if (entry.unit === unit) return;
    patchEntry(item.id, entry.id, { unit });
    try {
      if (String(entry.id).startsWith("legacy-")) {
        await api.updatePantryItem(item.id, { unit });
      } else {
        await api.updatePantryLocationEntry(item.id, entry.id, { unit });
      }
    } catch {
      patchEntry(item.id, entry.id, { unit: entry.unit });
      toast.error("Feil ved endring av enhet");
    }
  };

  const addLocationLine = async (item) => {
    const defaultLoc = activeLocId !== "all" ? activeLocId : (locations[0]?.id || "");
    try {
      const res = await api.addPantryLocationEntry(item.id, {
        location_id: defaultLoc,
        amount: "1",
        unit: item.unit || "stk",
        in_stock: true,
      });
      setItems((prev) => prev.map((i) => (i.id === item.id ? res.data : i)));
      toast.success("Ny lagerplass lagt til");
    } catch {
      toast.error("Feil ved tilføying");
    }
  };

  const deleteLocationLine = async (item, entry) => {
    const entries = entriesOf(item);
    if (entries.length <= 1) {
      // Last line — confirm deletion of entire item
      if (!window.confirm(`Dette er siste lagerplass. Slette hele varen "${item.name}"?`)) return;
      return handleDelete(item.id);
    }
    try {
      if (String(entry.id).startsWith("legacy-")) {
        await api.updatePantryItem(item.id, { amount: "", location_id: "", expiry_date: "" });
      } else {
        await api.deletePantryLocationEntry(item.id, entry.id);
      }
      setItems((prev) => prev.map((i) => {
        if (i.id !== item.id) return i;
        const remaining = entriesOf(i).filter((e) => e.id !== entry.id);
        return { ...i, locations: remaining };
      }));
      toast.success("Lagerplass fjernet fra varen");
    } catch {
      toast.error("Feil ved sletting av lagerplass");
    }
  };

  // ─── Locations ───
  const addLocation = async () => {
    if (!newLoc.name.trim()) return;
    try {
      await api.createStorageLocation(newLoc);
      setNewLoc({ name: "", type: "fridge" }); loadData();
      toast.success("Lagringsplass opprettet");
    } catch { toast.error("Feil ved oppretting"); }
  };
  const deleteLocation = async (id) => {
    try { await api.deleteStorageLocation(id); loadData(); toast.success("Lagringsplass slettet"); }
    catch { toast.error("Feil ved sletting"); }
  };

  const handleTabDrop = async (targetIdx) => {
    if (draggedIdx === null || draggedIdx === targetIdx) return;
    const reordered = [...locations];
    const [moved] = reordered.splice(draggedIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    // Optimistic update
    setLocations(reordered);
    setDraggedIdx(null);
    setDragOverIdx(null);
    try {
      await api.reorderStorageLocations(reordered.map((l) => l.id));
    } catch {
      toast.error("Kunne ikke lagre ny rekkefølge");
      loadData();
    }
  };

  const handleChipDrop = async (targetIdx) => {
    if (chipDraggedIdx === null || chipDraggedIdx === targetIdx) return;
    const reordered = [...activeCategoryList];
    const [moved] = reordered.splice(chipDraggedIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    setChipDraggedIdx(null);
    setChipDragOverIdx(null);
    if (!activeBoat?.id) return;
    try {
      await api.updateBoatCategoryOrder(activeBoat.id, reordered);
      await loadActiveBoat();
      toast.success("Kategori-rekkefølge lagret");
    } catch {
      toast.error("Kunne ikke lagre rekkefølge");
    }
  };

  // ─── Withdrawal ───
  const openWithdraw = (item, entry) => {
    setWithdrawItem({ ...item, _entry: entry });
    setWithdrawForm({ amount: entry?.amount || "", reason: "" });
    setWithdrawOpen(true);
  };
  const handleWithdraw = async () => {
    if (!withdrawItem) return;
    try {
      await api.createWithdrawal({
        pantry_item_id: withdrawItem.id,
        entry_id: withdrawItem._entry?.id && !String(withdrawItem._entry.id).startsWith("legacy-") ? withdrawItem._entry.id : "",
        amount: withdrawForm.amount,
        reason: withdrawForm.reason,
      });
      toast.success(`${withdrawItem.name} tatt ut`);
      setWithdrawOpen(false); setWithdrawItem(null); loadData();
    } catch { toast.error("Feil ved uttak"); }
  };

  // ─── Expiry helpers ───
  const daysUntilExpiry = (d) => {
    if (!d) return null;
    const date = new Date(d);
    if (isNaN(date)) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return Math.round((date - today) / 86400000);
  };
  const isExpiringOrExpired = (item) => {
    const entries = entriesOf(item);
    return entries.some((e) => {
      const d = daysUntilExpiry(e.expiry_date);
      return d != null && d <= 7;
    });
  };

  // ─── Grouping ───
  const normalizeCat = (c) => {
    if (!c) return "Annet";
    // Kollaps legacy-koder til kanonisk norsk
    if (LEGACY_TO_CANONICAL[c]) return LEGACY_TO_CANONICAL[c];
    if (CATEGORIES.includes(c)) return c;
    return "Annet";
  };
  const isInStock = (it) => {
    const entries = entriesOf(it);
    return entries.some((e) => e.in_stock !== false);
  };
  let filteredList = items;
  // Client-side location filter (ignored when searching so matches across all
  // locations show up).
  if (activeLocId !== "all" && !searchQuery.trim()) {
    filteredList = filteredList.filter((it) => entriesOf(it).some((e) => e.location_id === activeLocId));
  }
  if (searchQuery.trim()) {
    const q = searchQuery.trim().toLowerCase();
    filteredList = filteredList.filter((it) => (it.name || "").toLowerCase().includes(q) || (it.note || "").toLowerCase().includes(q));
  }
  if (expiringOnly) filteredList = filteredList.filter(isExpiringOrExpired);
  if (inStockOnly) filteredList = filteredList.filter(isInStock);
  const groupedByCategory = filteredList.reduce((acc, i) => {
    const cat = normalizeCat(i.category);
    // When searching, ignore the active-category chip — show ALL matches across categories.
    if (activeCategory !== "all" && cat !== activeCategory && !searchQuery.trim()) return acc;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(i);
    return acc;
  }, {});
  // Sort items within each category by earliest expiry (soonest first) when in expiring mode
  if (expiringOnly) {
    Object.keys(groupedByCategory).forEach((k) => {
      groupedByCategory[k].sort((a, b) => {
        const earliest = (it) => Math.min(...entriesOf(it).map((e) => daysUntilExpiry(e.expiry_date) ?? 9999));
        return earliest(a) - earliest(b);
      });
    });
  } else if (pantrySort === "alpha") {
    Object.keys(groupedByCategory).forEach((k) => {
      groupedByCategory[k].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "nb", { sensitivity: "base" })
      );
    });
  }

  const countByCategory = items.reduce((acc, i) => {
    const cat = normalizeCat(i.category);
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});
  // Ordering: saved order first (if present), then remaining by count desc
  const savedCatOrder = activeBoat?.category_order || [];
  const presentCats = Object.keys(countByCategory);
  const activeCategoryList = (() => {
    const ordered = savedCatOrder.filter((c) => presentCats.includes(c));
    const rest = presentCats.filter((c) => !ordered.includes(c)).sort((a, b) => countByCategory[b] - countByCategory[a]);
    return [...ordered, ...rest];
  })();

  const expiringCount = items.filter(isExpiringOrExpired).length;
  const inStockCount = items.filter(isInStock).length;

  const countByLoc = locations.reduce((acc, loc) => {
    acc[loc.id] = items.filter((i) => entriesOf(i).some((e) => e.location_id === loc.id)).length;
    return acc;
  }, {});

  const isExpiringSoon = (d) => { if (!d) return false; const diff = (new Date(d) - new Date()) / 86400000; return diff >= 0 && diff <= 3; };
  const isExpired = (d) => { if (!d) return false; return new Date(d) < new Date(); };

  // ─── Name autocomplete ───
  useEffect(() => {
    if (!formOpen) { setNameSuggestions([]); setSuggestionsOpen(false); return; }
    const q = form.name.trim();
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setSuggestLoading(true);
        const res = await api.getPantryNameSuggestions(q);
        if (cancelled) return;
        const items = res.data?.items || [];
        // Hide suggestions if the only match is exactly the current input (no value to show)
        const normalized = q.toLowerCase();
        const filtered = items.filter((it) => it.name.toLowerCase() !== normalized);
        setNameSuggestions(filtered.slice(0, 8));
      } catch {
        if (!cancelled) setNameSuggestions([]);
      } finally {
        if (!cancelled) setSuggestLoading(false);
      }
    }, 180);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.name, formOpen]);

  const selectSuggestion = (s) => {
    setForm((prev) => ({
      ...prev,
      name: s.name,
      category: s.category && s.category !== "Other" ? s.category : prev.category,
    }));
    setSuggestionsOpen(false);
  };

  const markAsWaste = async (item, entry) => {
    const suffix = entry && !String(entry.id).startsWith("legacy-") ? ` (${getLocName(entry.location_id) || "plass"})` : "";
    if (!window.confirm(`Marker "${item.name}${suffix}" som svinn? Varen fjernes fra beholdning og logges i matsvinn-rapporten.`)) return;
    try {
      await api.createWithdrawal({
        pantry_item_id: item.id,
        entry_id: entry && !String(entry.id).startsWith("legacy-") ? entry.id : "",
        amount: entry?.amount || item.amount || "",
        reason: "Svinn - utgått",
      });
      toast.success(`${item.name} markert som svinn`);
      loadData();
    } catch {
      toast.error("Feil ved logging av svinn");
    }
  };

  // ─── Add to shopping list ───
  const addToShopping = async (item) => {
    try {
      const res = await api.addPantryToShopping(item.id);
      if (res.data?.status === "already_in_list") {
        toast.info(`${item.name} ligger allerede i handlelisten`);
      } else {
        toast.success(`${item.name} lagt til i handlelisten`);
      }
    } catch {
      toast.error("Feil ved tilføying");
    }
  };

  // ─── Toggle in_stock ("Tomt / Ikke om bord") per entry ───
  const toggleInStock = async (item, entry) => {
    const next = !(entry.in_stock !== false);
    patchEntry(item.id, entry.id, { in_stock: next });
    try {
      if (String(entry.id).startsWith("legacy-")) {
        await api.updatePantryItem(item.id, { in_stock: next });
      } else {
        await api.updatePantryLocationEntry(item.id, entry.id, { in_stock: next });
      }
      toast.success(next ? `${item.name} markert som ${stockLabel}` : `${item.name} markert som tomt / ikke ${stockLabel}`);
    } catch {
      patchEntry(item.id, entry.id, { in_stock: !next });
      toast.error("Feil ved oppdatering");
    }
  };

  // ─── Apply ?expiring=1 on mount ───
  useEffect(() => {
    if (searchParams.get("expiring") === "1") {
      setExpiringOnly(true);
      // Remove the param after consuming so it doesn't persist
      const next = new URLSearchParams(searchParams);
      next.delete("expiring");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="pantry-page">
      <PantrySubTabs />
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Beholdning</h1>
          <p className="text-sm text-stone-500 mt-1">{items.length} varer på lager</p>
        </div>
        <div className="flex items-center gap-3" ref={moreMenuRef}>
          <button onClick={() => setBarcodeOpen(true)} className="inline-flex items-center justify-center rounded-md border border-stone-200 bg-white px-3 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors gap-2" title="Skann strekkode for å legge til vare" data-testid="pantry-barcode-btn">
            <Camera className="h-4 w-4" /> <span className="hidden sm:inline">Skann</span>
          </button>
          <button
            onClick={() => setCountingOpen(true)}
            disabled={items.length === 0}
            className="inline-flex items-center justify-center rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2.5 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 transition-colors gap-2 disabled:opacity-50"
            title="Gå gjennom beholdningen vare for vare og bekreft / endre / fjern"
            data-testid="start-counting-btn"
          >
            <PackageCheck className="h-4 w-4" /> <span>Start opptelling</span>
          </button>
          <button onClick={openCreate} className="inline-flex items-center justify-center rounded-md bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors gap-2" data-testid="add-pantry-item-btn">
            <Plus className="h-4 w-4" /> Legg til vare
          </button>
          <button
            onClick={() => setQuickAddOpen(true)}
            className="inline-flex items-center justify-center rounded-md border border-violet-300 bg-violet-50 px-3 py-2.5 text-sm font-semibold text-violet-800 hover:bg-violet-100 transition-colors gap-2"
            title="Legg til varer du har kjøpt før — eitt trykk per vare"
            data-testid="quick-add-btn"
          >
            <Zap className="h-4 w-4" /> Hurtiglegg
          </button>
          <div className="relative">
            <button
              onClick={() => setMoreMenuOpen(!moreMenuOpen)}
              className="relative inline-flex items-center justify-center rounded-md border border-stone-200 bg-white p-2.5 text-stone-600 hover:bg-stone-50 transition-colors"
              data-testid="pantry-more-menu-btn"
              title={cleanupBadgeCount > 3 ? `${cleanupBadgeCount} ting kan ryddes opp` : "Flere valg"}
              aria-label="Flere valg"
            >
              <MoreVertical className="h-4 w-4" />
              {cleanupBadgeCount > 3 && (
                <span
                  className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold border-2 border-white"
                  data-testid="cleanup-badge"
                  title={`${cleanupBadgeCount} ting kan ryddes opp i beholdninga`}
                >
                  {cleanupBadgeCount > 99 ? "99+" : cleanupBadgeCount}
                </span>
              )}
            </button>
            {moreMenuOpen && (
              <div className="absolute right-0 top-full mt-1 z-30 w-60 rounded-lg border border-stone-200 bg-white shadow-lg overflow-hidden" data-testid="pantry-more-menu">
                <button
                  onClick={() => { setCleanupOpen(true); setMoreMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-violet-700 hover:bg-violet-50 font-semibold border-b border-stone-100"
                  data-testid="open-cleanup-dialog-btn"
                  title="Forenkle navn, hent butikkbilete, finn duplikat"
                >
                  <Sparkles className="h-4 w-4 text-violet-600" />
                  Rydd opp i beholdning
                </button>
                <button
                  onClick={() => { setAutoFixImagesOpen(true); setMoreMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-emerald-700 hover:bg-emerald-50 font-semibold border-b border-stone-100"
                  data-testid="open-autofix-images-btn"
                  title="Hent butikkbilete for alle varer som manglar bilde"
                >
                  <Sparkles className="h-4 w-4 text-emerald-600" />
                  Auto-fiks bilder
                </button>
                <button
                  onClick={() => { setShowHistory(!showHistory); setMoreMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-stone-700 hover:bg-stone-50"
                  data-testid="toggle-history-btn"
                >
                  <History className="h-4 w-4 text-stone-500" />
                  <span>{showHistory ? "Skjul uttakslogg" : "Uttakslogg"}</span>
                </button>
                <button
                  onClick={() => { setLocDialogOpen(true); setMoreMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-stone-700 hover:bg-stone-50"
                  data-testid="manage-locations-btn"
                >
                  <Settings className="h-4 w-4 text-stone-500" />
                  Lagringsplasser
                </button>
                {items.length > 0 && (
                  <button
                    onClick={() => { handleAutoSort(); setMoreMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-stone-700 hover:bg-stone-50"
                    data-testid="ai-sort-btn"
                  >
                    <Sparkles className="h-4 w-4 text-seawater-600" />
                    AI-sorter kategorier
                  </button>
                )}
                <button
                  onClick={() => { handleRemoveEmpty(); setMoreMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-stone-700 hover:bg-stone-50"
                  data-testid="remove-empty-pantry-btn"
                  title="Fjern alle varer med mengde 0"
                >
                  <PackageX className="h-4 w-4 text-amber-600" />
                  Fjern tomme varer
                </button>
                <button
                  onClick={() => { openStandardizeDialog(); setMoreMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-stone-700 hover:bg-stone-50"
                  data-testid="standardize-names-btn"
                  title="Fjern butikk/merke fra varenavn"
                >
                  <Sparkles className="h-4 w-4 text-violet-600" />
                  Standardiser varenavn
                </button>
                {items.length > 0 && (
                  <button
                    onClick={() => { handleOneClickClean(); setMoreMenuOpen(false); }}
                    disabled={oneClickCleanLoading}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-60"
                    data-testid="one-click-clean-btn"
                    title="Strip merker + slå saman like varer i èi handling"
                  >
                    <Sparkles className="h-4 w-4 text-emerald-600" />
                    <span className="flex-1 text-left">{oneClickCleanLoading ? "Ryddar opp…" : "Rydd opp heile beholdninga"}</span>
                    {cleanupBadgeCount > 0 && !oneClickCleanLoading && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold" data-testid="cleanup-count-pill">
                        {cleanupBadgeCount}
                      </span>
                    )}
                  </button>
                )}
                <button
                  onClick={() => { setTrashOpen(true); setMoreMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-stone-700 hover:bg-stone-50"
                  data-testid="open-pantry-trash-btn"
                  title="Se slettede varer (gjenopprettelig i 30 dager)"
                >
                  <ArchiveRestore className="h-4 w-4 text-amber-600" />
                  Papirkurv
                </button>
                {items.length > 0 && (
                  <>
                    <div className="border-t border-stone-100" />
                    <button
                      onClick={() => { setDeletePinOpen(true); setMoreMenuOpen(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50"
                      data-testid="delete-all-pantry-btn"
                    >
                      <Trash2 className="h-4 w-4" />
                      Tøm hele beholdningen
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Location tabs */}
      <DuplicateBanner
        fetchFn={api.getPantryDuplicates}
        mergeFn={api.mergePantryDuplicates}
        aiFetchFn={api.aiFindPantryDuplicates}
        rejectFn={api.rejectPantryDuplicate}
        title="duplikater i beholdningen"
        onMerged={loadData}
        testIdPrefix="pantry-dupe"
      />

      {/* Search bar */}
      <div className="relative mb-3" data-testid="pantry-search-wrapper">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Søk i beholdningen (navn eller notat)…"
          className="w-full rounded-lg border border-stone-200 bg-white pl-9 pr-9 py-2 text-sm text-stone-700 placeholder:text-stone-400 focus:border-navy-800 focus:outline-none"
          data-testid="pantry-search-input"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-stone-100 text-stone-400 hover:text-stone-600"
            data-testid="pantry-search-clear"
            title="Tøm søk"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {!searchQuery.trim() && (
      <div className="flex gap-1.5 mb-3 items-center overflow-x-auto sm:flex-wrap sm:gap-2 scrollbar-thin -mx-2 px-2 pb-1 sm:mx-0 sm:px-0 sm:pb-0">
        <button onClick={() => setActiveLocId("all")} className={`flex items-center gap-1.5 px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium transition-all border whitespace-nowrap flex-shrink-0 ${activeLocId === "all" ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"}`} data-testid="location-tab-all">
          <Package className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> Alle <span className="text-[10px] sm:text-xs opacity-70">({items.length})</span>
        </button>
        {locations.map((loc, idx) => {
          const isStaging = loc.name === "Fra kvittering";
          const meta = isStaging
            ? { icon: Receipt, color: "bg-orange-50 text-orange-700 border-orange-200", label: "Fra kvittering" }
            : (TYPE_META[loc.type] || TYPE_META.fridge);
          const Icon = meta.icon;
          const isActive = activeLocId === loc.id;
          const count = countByLoc[loc.id] || 0;
          // Skip empty tabs on mobile to save space — they re-appear when the
          // tab becomes active or once it gets at least one item.
          if (count === 0 && !isActive && !isStaging) return null;
          const isDragTarget = dragOverIdx === idx;
          return (
            <div
              key={loc.id}
              draggable
              onDragStart={(e) => { setDraggedIdx(idx); e.dataTransfer.effectAllowed = "move"; }}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragOverIdx !== idx) setDragOverIdx(idx); }}
              onDragLeave={() => { if (dragOverIdx === idx) setDragOverIdx(null); }}
              onDrop={(e) => { e.preventDefault(); handleTabDrop(idx); }}
              onDragEnd={() => { setDraggedIdx(null); setDragOverIdx(null); }}
              className={`inline-flex items-stretch rounded-lg sm:rounded-xl overflow-hidden border group cursor-grab active:cursor-grabbing transition-all whitespace-nowrap flex-shrink-0 ${isDragTarget && draggedIdx !== idx ? "ring-2 ring-seawater-500 scale-105" : ""} ${draggedIdx === idx ? "opacity-40" : ""}`}
              data-testid={`location-tab-wrap-${loc.id}`}
            >
              <button onClick={() => setActiveLocId(loc.id)}
                className={`flex items-center gap-1.5 pl-2.5 pr-2 py-1.5 sm:pl-4 sm:pr-3 sm:py-2 text-xs sm:text-sm font-medium transition-all ${isActive ? "bg-stone-900 text-white" : `${meta.color} hover:opacity-80`}`}
                data-testid={`location-tab-${loc.id}`}>
                <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> {loc.name}
                {activeLocId === "all" && <span className="text-[10px] sm:text-xs opacity-70">({count})</span>}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (count > 0) {
                    if (!window.confirm(`"${loc.name}" inneholder ${count} varer. Fjerne fanen? Varene blir liggende under "Alle" uten lagringsplass.`)) return;
                  } else if (!window.confirm(`Fjerne fanen "${loc.name}"?`)) return;
                  deleteLocation(loc.id);
                  if (isActive) setActiveLocId("all");
                }}
                className={`px-2 border-l opacity-0 group-hover:opacity-100 transition-opacity ${isActive ? "bg-stone-900 text-white border-white/20 hover:bg-red-700" : "bg-white/60 text-stone-400 border-stone-200 hover:bg-red-50 hover:text-red-600"}`}
                title={`Fjern fanen ${loc.name}`}
                data-testid={`remove-location-tab-${loc.id}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
        <button
          onClick={() => setLocDialogOpen(true)}
          className="flex items-center gap-1 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium border border-dashed border-stone-300 bg-white text-seawater-700 hover:bg-sailcloth-100 hover:border-seawater-400 transition-all whitespace-nowrap flex-shrink-0"
          data-testid="add-location-tab-btn"
          title="Legg til ny fane (lagringsplass)"
        >
          <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> Ny
        </button>
      </div>
      )}

      {/* Expiring filter banner (shows when there are expiring items) */}
      {!searchQuery.trim() && (expiringCount > 0 || items.length > 0) && (
        <div className="mb-3 flex gap-2 overflow-x-auto sm:flex-wrap scrollbar-thin -mx-2 px-2 pb-1 sm:mx-0 sm:px-0 sm:pb-0" data-testid="expiring-filter-row">
          {expiringCount > 0 && (
            <button
              onClick={() => setExpiringOnly(!expiringOnly)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border whitespace-nowrap flex-shrink-0 ${
                expiringOnly
                  ? "bg-red-600 text-white border-red-600 shadow-sm"
                  : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
              }`}
              data-testid="expiring-filter-toggle"
            >
              <AlertTriangle className="h-4 w-4" />
              {expiringOnly ? "Viser kun utløper snart" : "Utløper snart"} ({expiringCount})
            </button>
          )}
          {items.length > 0 && (
            <button
              onClick={() => setInStockOnly(!inStockOnly)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border whitespace-nowrap flex-shrink-0 ${
                inStockOnly
                  ? "bg-navy-800 text-white border-navy-800 shadow-sm"
                  : "bg-sailcloth-100 text-navy-800 border-navy-100 hover:bg-navy-50"
              }`}
              data-testid="instock-filter-toggle"
              title={`Vis kun varer som er merket som ${stockLabel}`}
            >
              <PackageCheck className="h-4 w-4" />
              {inStockOnly ? `Viser kun ${stockLabel}` : `Har ${stockLabel}`} ({inStockCount})
            </button>
          )}
          {items.length > 0 && (
            <button
              onClick={() => setPantrySort(pantrySort === "alpha" ? "default" : "alpha")}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border whitespace-nowrap flex-shrink-0 ${
                pantrySort === "alpha"
                  ? "bg-navy-800 text-white border-navy-800 shadow-sm"
                  : "bg-sailcloth-100 text-navy-800 border-navy-100 hover:bg-navy-50"
              }`}
              data-testid="pantry-sort-alpha"
              title="Sorter varene alfabetisk A-Å innenfor hver kategori"
            >
              A-Å
            </button>
          )}
        </div>
      )}
      {!searchQuery.trim() && activeCategoryList.length > 0 && (
        <div className="flex gap-2 mb-6 items-center overflow-x-auto sm:flex-wrap scrollbar-thin -mx-2 px-2 pb-1 sm:mx-0 sm:px-0 sm:pb-0" data-testid="category-filter-chips">
          <button onClick={() => setActiveCategory("all")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border whitespace-nowrap flex-shrink-0 ${activeCategory === "all" ? "bg-navy-800 text-white border-navy-800" : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"}`} data-testid="category-chip-all">
            Alle kategorier <span className="opacity-70">({items.length})</span>
          </button>
          {(() => {
            const subgroups = activeBoat?.category_subgroups || {};
            // Build display order: top-level chips in their saved order, then each
            // chip's child categories (also limited to those present) right after.
            const present = new Set(activeCategoryList);
            const topLevel = activeCategoryList.filter((c) => {
              const p = subgroups[c];
              return !p || !present.has(p) || p === c;
            });
            const children = activeCategoryList.filter((c) => topLevel.indexOf(c) === -1);
            const displayList = [];
            topLevel.forEach((parent) => {
              displayList.push({ cat: parent, isChild: false });
              children
                .filter((c) => subgroups[c] === parent)
                .forEach((child) => displayList.push({ cat: child, isChild: true }));
            });
            // Append any orphaned children (parent missing) at the end as top-level
            children.forEach((c) => {
              if (!displayList.find((d) => d.cat === c)) displayList.push({ cat: c, isChild: false });
            });
            return displayList.map(({ cat, isChild }) => {
              const idx = activeCategoryList.indexOf(cat);
              const isDragTarget = chipDragOverIdx === idx;
              return (
                <button
                  key={cat}
                  draggable
                  onDragStart={(e) => { setChipDraggedIdx(idx); e.dataTransfer.effectAllowed = "move"; }}
                  onDragOver={(e) => { e.preventDefault(); if (chipDragOverIdx !== idx) setChipDragOverIdx(idx); }}
                  onDragLeave={() => { if (chipDragOverIdx === idx) setChipDragOverIdx(null); }}
                  onDrop={(e) => { e.preventDefault(); handleChipDrop(idx); }}
                  onDragEnd={() => { setChipDraggedIdx(null); setChipDragOverIdx(null); }}
                  onClick={() => setActiveCategory(cat)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-grab active:cursor-grabbing whitespace-nowrap flex-shrink-0 ${isChild ? "ml-3 bg-stone-50" : "bg-white"} ${activeCategory === cat ? "bg-navy-800 text-white border-navy-800" : "text-stone-600 border-stone-200 hover:bg-stone-50"} ${isDragTarget && chipDraggedIdx !== idx ? "ring-2 ring-seawater-500 scale-105" : ""} ${chipDraggedIdx === idx ? "opacity-40" : ""}`}
                  data-testid={`category-chip-${cat}`}
                >
                  {isChild && <span className="text-stone-400">↳</span>}
                  {CATEGORY_LABELS[cat] || cat} <span className="opacity-70">({countByCategory[cat]})</span>
                </button>
              );
            });
          })()}
          {activeBoat?.id && (
            <button
              type="button"
              onClick={() => setEditGroupsOpen(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-stone-300 text-stone-500 hover:bg-stone-50 hover:text-navy-800 transition-colors whitespace-nowrap flex-shrink-0"
              title="Rediger fane-rekkefølge og undergrupper"
              data-testid="edit-category-groups-btn"
            >
              <Settings className="h-3 w-3" /> Rediger faner
            </button>
          )}
        </div>
      )}

      {/* Withdrawal history */}
      {showHistory && (
        <div className="mb-6 animate-fade-in" data-testid="withdrawal-history">
          <h3 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-3 flex items-center gap-2"><ArrowDownToLine className="h-3.5 w-3.5" /> Uttakslogg</h3>
          {withdrawals.length === 0 ? (
            <p className="text-sm text-stone-400 bg-white border border-stone-100 rounded-xl p-4">Ingen uttak registrert ennå</p>
          ) : (
            <div className="bg-white border border-stone-100 rounded-xl divide-y divide-stone-100 max-h-64 overflow-y-auto">
              {withdrawals.map((w) => (
                <div key={w.id} className="flex items-center gap-3 px-4 py-3" data-testid={`withdrawal-${w.id}`}>
                  <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0 border border-orange-200">
                    <ArrowDownToLine className="h-4 w-4 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-stone-700">{w.item_name}</span>
                      {(w.amount || w.unit) && <span className="text-xs text-stone-400">{w.amount} {w.unit}</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-stone-400">{w.date}</span>
                      {w.location_name && <span className="text-xs text-stone-400">fra {w.location_name}</span>}
                      {w.reason && <span className="text-xs text-stone-500">– {w.reason}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Items */}
      {(() => {
        const stagingLoc = locations.find((l) => l.name === "Fra kvittering");
        const stagingCount = stagingLoc ? (countByLoc[stagingLoc.id] || 0) : 0;
        if (stagingCount > 0 && stagingLoc && activeLocId === stagingLoc.id) {
          const otherLocs = locations.filter((l) => l.id !== stagingLoc.id);
          const handleMoveAll = async (targetLocId) => {
            if (!targetLocId) return;
            try {
              await api.movePantryLocationEntries(stagingLoc.id, targetLocId);
              const target = locations.find((l) => l.id === targetLocId);
              toast.success(`${stagingCount} varer flyttet til ${target?.name || "valgt lager"}`);
              loadData();
              setActiveLocId(targetLocId);
            } catch (err) {
              toast.error(err.response?.data?.detail || "Kunne ikke flytte varer");
            }
          };
          return (
            <div className="mb-3 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5" data-testid="staging-info">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="text-xs text-orange-800 flex-1 min-w-0">
                  <Receipt className="inline h-3.5 w-3.5 -mt-0.5 mr-1" />
                  <strong>{stagingCount} varer fra kvittering venter på sortering.</strong>{" "}
                  <span className="hidden sm:inline">Flytt alle på én gang eller bruk nedtrekket per vare.</span>
                </div>
                {otherLocs.length > 0 && (
                  <div className="flex items-center gap-2 flex-shrink-0" data-testid="staging-bulk-move-wrapper">
                    <span className="text-xs text-orange-700 font-semibold">Flytt alle til:</span>
                    <select
                      onChange={(e) => { const v = e.target.value; e.target.value = ""; handleMoveAll(v); }}
                      defaultValue=""
                      className="rounded-md border border-orange-300 bg-white px-2 py-1 text-xs text-stone-700 focus:outline-none focus:border-navy-800"
                      data-testid="staging-bulk-move-select"
                    >
                      <option value="" disabled>Velg lager…</option>
                      {otherLocs.map((l) => (
                        <option key={l.id} value={l.id}>{l.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          );
        }
        return null;
      })()}
      {searchQuery.trim() && Object.keys(groupedByCategory).length > 0 && (
        <div className="mb-2 flex items-center justify-between gap-2" data-testid="pantry-search-results-header">
          <h3 className="text-xs uppercase tracking-[0.2em] text-navy-700 font-semibold">
            Søkeresultat for «{searchQuery.trim()}»
          </h3>
          <span className="text-xs text-stone-500">
            {Object.values(groupedByCategory).reduce((sum, arr) => sum + arr.length, 0)} treff
          </span>
        </div>
      )}
      {/* Verification banner — vises når mange varer trenger opptelling */}
      {!searchQuery.trim() && (verificationStatus.overdue_count + verificationStatus.never_verified_count) >= 3 && (
        <div
          className="mb-4 flex items-center gap-3 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 px-4 py-3"
          data-testid="verification-banner"
        >
          <PackageCheck className="h-6 w-6 text-amber-700 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              {verificationStatus.overdue_count + verificationStatus.never_verified_count} varer trenger opptelling
            </p>
            <p className="text-xs text-amber-700/80 mt-0.5">
              {verificationStatus.overdue_count > 0 && `${verificationStatus.overdue_count} eldre enn ${verificationThreshold} dager`}
              {verificationStatus.overdue_count > 0 && verificationStatus.never_verified_count > 0 && " · "}
              {verificationStatus.never_verified_count > 0 && `${verificationStatus.never_verified_count} aldri talt`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCountingOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 px-3 py-2 text-xs font-semibold text-white whitespace-nowrap"
            data-testid="banner-start-counting-btn"
          >
            <PackageCheck className="h-3.5 w-3.5" /> Start opptelling
          </button>
        </div>
      )}
      {Object.keys(groupedByCategory).length === 0 ? (
        <div className="text-center py-16" data-testid="empty-pantry">
          <Refrigerator className="h-12 w-12 mx-auto text-stone-300 mb-4" />
          <h3 className="text-lg font-medium text-stone-600" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {searchQuery.trim() ? `Ingen treff på «${searchQuery.trim()}»` : (activeLocId === "all" ? "Beholdningen er tom" : "Ingen varer her")}
          </h3>
          <p className="text-sm text-stone-400 mt-1">{searchQuery.trim() ? "Prøv et annet søkeord" : "Legg til varer du har hjemme"}</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="pantry-items">
          {Object.entries(groupedByCategory).map(([category, catItems]) => (
            <div key={category} data-testid={`pantry-category-${category.toLowerCase()}`}>
              <h3 className="sticky top-0 z-10 -mx-2 px-2 py-2 bg-sailcloth-100/90 backdrop-blur-sm text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-3 rounded flex items-center justify-between gap-2" data-testid={`pantry-category-label-${category.toLowerCase()}`}>
                <span>{CATEGORY_LABELS[category] || category} <span className="text-stone-400 font-normal ml-1">({catItems.length})</span></span>
                <button
                  type="button"
                  onClick={() => handleVerifyCategory(CATEGORY_LABELS[category] || category, catItems)}
                  className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white px-2.5 py-0.5 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400 transition-colors normal-case tracking-normal"
                  title={`Bekreft alle ${catItems.length} varer i ${CATEGORY_LABELS[category] || category} som opptelt nå`}
                  data-testid={`verify-all-${category.toLowerCase()}`}
                >
                  <Check className="h-3 w-3" />
                  Bekreft alle
                </button>
              </h3>
              <div className="space-y-2">
                {catItems.map((item) => {
                  const entries = entriesOf(item);
                  // Filter entries by active location, UNLESS user is searching
                  // (in which case we want all matching entries visible regardless of tab).
                  const visibleEntries = (activeLocId === "all" || searchQuery.trim())
                    ? entries
                    : entries.filter((e) => e.location_id === activeLocId);
                  if (visibleEntries.length === 0) return null;
                  // Compute totals across entries sharing a unit
                  const unitTotals = visibleEntries.reduce((acc, e) => {
                    const n = parseFloat((e.amount || "0").toString().replace(",", "."));
                    if (!isNaN(n) && e.unit) acc[e.unit] = (acc[e.unit] || 0) + n;
                    return acc;
                  }, {});
                  const totalsLabel = Object.entries(unitTotals)
                    .map(([u, n]) => `${+n.toFixed(2)} ${u}`).join(" + ");
                  const anyExpired = visibleEntries.some((e) => isExpired(e.expiry_date));
                  const allOutOfStock = visibleEntries.every((e) => e.in_stock === false);
                  return (
                    <div key={item.id} className={`relative bg-white border border-stone-100 rounded-xl overflow-hidden ${anyExpired ? "ring-1 ring-red-200" : ""} ${allOutOfStock ? "opacity-70" : ""}`} data-testid={`pantry-item-${item.id}`}>
                      {/* Alder-stripe — visuell indikator på hvor lenge sidan vara blei talt */}
                      <AgeStripe item={item} thresholdDays={verificationThreshold} />
                      {/* VerifyDot — alltid oppe til høyre på kortet, frittstående frå header-rada */}
                      <div className="absolute right-2 top-3 z-10 pointer-events-auto">
                        <VerifyDot
                          item={item}
                          thresholdDays={verificationThreshold}
                          onVerify={handleVerifyItem}
                          verifying={verifyingIds.has(item.id)}
                        />
                      </div>
                      {/* Item header */}
                      <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 bg-sailcloth-100/50 border-b border-stone-100">
                        <button
                          type="button"
                          onClick={() => openImageEditor(item)}
                          className="relative h-10 w-10 sm:h-12 sm:w-12 rounded-md object-cover border border-stone-200 flex-shrink-0 overflow-hidden bg-stone-100 group/img"
                          title={item.image_url ? "Endre eller generer nytt bilde" : "Legg til bilde (AI eller manuelt)"}
                          data-testid={`pantry-item-image-btn-${item.id}`}
                        >
                          {item.image_url ? (
                            <img
                              src={item.image_url}
                              alt={item.name}
                              className="w-full h-full object-cover"
                              data-testid={`pantry-item-image-${item.id}`}
                              loading="lazy"
                            />
                          ) : (
                            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-stone-400 m-auto group-hover/img:text-violet-600 transition-colors" />
                          )}
                          <span className="absolute inset-0 bg-black/0 group-hover/img:bg-black/40 flex items-center justify-center text-white opacity-0 group-hover/img:opacity-100 transition-all">
                            <Pencil className="h-3.5 w-3.5" />
                          </span>
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className={`text-sm font-semibold ${anyExpired ? "text-red-600" : allOutOfStock ? "text-stone-500 line-through" : "text-stone-800"}`} data-testid={`pantry-item-name-${item.id}`}>{item.name}</span>
                            {totalsLabel && (
                              <span className="text-xs text-stone-500">Totalt: <span className="font-medium text-stone-700">{totalsLabel}</span></span>
                            )}
                            {visibleEntries.length > 1 && (
                              <span className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">{visibleEntries.length} plasser</span>
                            )}
                          </div>
                          {Array.isArray(item.brands) && item.brands.length > 0 && (
                            <div className="text-[10px] italic text-stone-400 mt-0.5" data-testid={`pantry-item-brands-${item.id}`} title="Merker/produsenter">
                              {item.brands.join(" · ")}
                            </div>
                          )}
                          {/* Inline kategori-velger — på mobil ligg den UNDER namnet,
                              på desktop går den i action-rada til høgre. */}
                          <select
                            value={CATEGORIES.includes(item.category) ? item.category : "Annet"}
                            onChange={async (e) => {
                              const newCat = e.target.value;
                              if (newCat === item.category) return;
                              try {
                                await api.updatePantryItem(item.id, { category: newCat });
                                try {
                                  await api.learnPantryCategory(item.name || "", newCat, "boat");
                                } catch (le) { console.warn("Kunne ikkje lære pantry-kategori", le?.message); }
                                toast.success(`Flytta «${item.name}» til ${CATEGORY_LABELS[newCat] || newCat}`);
                                loadData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || "Kunne ikkje endre kategori");
                              }
                            }}
                            className="sm:hidden mt-1 text-[10px] text-stone-600 bg-white border border-stone-200 rounded px-1.5 py-1 hover:border-navy-300 focus:border-navy-600 outline-none max-w-[140px]"
                            data-testid={`pantry-edit-category-mobile-${item.id}`}
                            title="Endre kategori — AI hugsar valet"
                          >
                            {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
                          </select>
                        </div>
                        {/* Desktop: kategori-velger til høgre i action-rada */}
                        <select
                          value={CATEGORIES.includes(item.category) ? item.category : "Annet"}
                          onChange={async (e) => {
                            const newCat = e.target.value;
                            if (newCat === item.category) return;
                            try {
                              await api.updatePantryItem(item.id, { category: newCat });
                              try {
                                await api.learnPantryCategory(item.name || "", newCat, "boat");
                              } catch (le) { console.warn("Kunne ikkje lære pantry-kategori", le?.message); }
                              toast.success(`Flytta «${item.name}» til ${CATEGORY_LABELS[newCat] || newCat}`);
                              loadData();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Kunne ikkje endre kategori");
                            }
                          }}
                          className="hidden sm:block text-xs text-stone-600 bg-white border border-stone-200 rounded px-1.5 py-1 hover:border-navy-300 focus:border-navy-600 outline-none max-w-[110px]"
                          data-testid={`pantry-edit-category-${item.id}`}
                          title="Endre kategori — AI hugsar valet"
                        >
                          {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
                        </select>
                        <button
                          onClick={() => addToShopping(item)}
                          className="inline-flex items-center gap-1.5 rounded-md bg-navy-800 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-navy-900 transition-colors"
                          title="Legg varen i handlelisten"
                          data-testid={`add-to-shopping-${item.id}`}
                        >
                          <ShoppingCart className="h-3.5 w-3.5" /> Handle
                        </button>
                        <button
                          onClick={() => openImageEditor(item)}
                          className="p-1.5 hover:bg-violet-50 rounded-md text-stone-400 hover:text-violet-600 transition-colors"
                          title="AI-fiks bilde / endre bilde"
                          data-testid={`fix-image-${item.id}`}
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => openEdit(item)} className="p-1.5 hover:bg-stone-100 rounded-md text-stone-400 hover:text-navy-800 transition-colors" data-testid={`edit-pantry-${item.id}`} title="Rediger navn / kategori">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => handleDelete(item.id)} className="p-1.5 hover:bg-red-50 rounded-md text-stone-400 hover:text-red-500 transition-colors" data-testid={`delete-pantry-${item.id}`} title="Slett hele varen">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {/* Per-location lines */}
                      <div className="divide-y divide-stone-50">
                        {visibleEntries.map((entry) => {
                          const meta = getLocMeta(entry.location_id);
                          const LocIcon = meta.icon;
                          const expired = isExpired(entry.expiry_date);
                          const expiring = isExpiringSoon(entry.expiry_date);
                          const outOfStock = entry.in_stock === false;
                          return (
                            <div key={entry.id} className={`flex flex-row flex-wrap items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 transition-all ${outOfStock ? "bg-stone-50/50" : ""}`} data-testid={`pantry-entry-${entry.id}`}>
                              {/* Location picker */}
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border hover:ring-2 hover:ring-seawater-500 transition-all flex-shrink-0 ${meta.color}`} data-testid={`change-location-${entry.id}`} title="Klikk for å bytte lagringsplass">
                                    <LocIcon className="h-3 w-3" />
                                    {getLocName(entry.location_id) || "Ingen"}
                                    <ChevronDown className="h-3 w-3 opacity-60" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-52 p-1" align="start">
                                  <div className="space-y-0.5">
                                    {locations.map((loc) => {
                                      const lMeta = TYPE_META[loc.type] || TYPE_META.fridge;
                                      const LIcon = lMeta.icon;
                                      const isCurrent = entry.location_id === loc.id;
                                      return (
                                        <button
                                          key={loc.id}
                                          onClick={() => handleLocationChange(item, entry, loc.id)}
                                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${isCurrent ? "bg-sailcloth-100 text-navy-800 font-medium" : "text-stone-700 hover:bg-stone-50"}`}
                                          data-testid={`move-entry-${entry.id}-to-${loc.id}`}
                                        >
                                          <LIcon className="h-4 w-4" />
                                          <span className="truncate">{loc.name}</span>
                                          {isCurrent && <span className="ml-auto text-[10px]">✓</span>}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </PopoverContent>
                              </Popover>

                              {/* Quantity + Unit grouped (mobile-friendly: stays on one line) */}
                              <div className="inline-flex items-stretch gap-1 flex-shrink-0">
                                <div className="inline-flex items-center gap-0.5 rounded-full border border-stone-200 bg-white overflow-hidden" data-testid={`qty-${entry.id}`}>
                                  <button
                                    onClick={() => bumpAmount(item, entry, -1)}
                                    className="px-2 py-1 text-stone-500 hover:bg-stone-100 text-sm leading-none"
                                    title="Reduser"
                                    data-testid={`qty-dec-${entry.id}`}
                                  >−</button>
                                  <input
                                    key={`qty-${entry.id}-${entry.amount}`}
                                    type="text"
                                    inputMode="decimal"
                                    defaultValue={entry.amount || ""}
                                    onBlur={(e) => handleAmountChange(item, entry, e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                                    onFocus={(e) => e.target.select()}
                                    className="w-10 text-center text-xs font-semibold text-stone-700 bg-transparent outline-none focus:bg-sailcloth-100"
                                    data-testid={`qty-input-${entry.id}`}
                                  />
                                  <button
                                    onClick={() => bumpAmount(item, entry, 1)}
                                    className="px-2 py-1 text-stone-500 hover:bg-stone-100 text-sm leading-none"
                                    title="Øk"
                                    data-testid={`qty-inc-${entry.id}`}
                                  >+</button>
                                </div>

                                {/* Unit — right next to qty on ALL screen sizes */}
                                <Select value={entry.unit || "stk"} onValueChange={(v) => handleUnitChange(item, entry, v)}>
                                  <SelectTrigger className="h-7 w-[62px] text-xs px-2" data-testid={`unit-${entry.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {UNITS.map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}
                                  </SelectContent>
                                </Select>
                              </div>

                              {/* Expiry — grouped with clear button */}
                              <div className="inline-flex items-stretch flex-shrink-0">
                                <input
                                  type="date"
                                  value={entry.expiry_date || ""}
                                  onChange={async (e) => {
                                    const v = e.target.value;
                                    patchEntry(item.id, entry.id, { expiry_date: v });
                                    try {
                                      if (String(entry.id).startsWith("legacy-")) {
                                        await api.updatePantryItem(item.id, { expiry_date: v });
                                      } else {
                                        await api.updatePantryLocationEntry(item.id, entry.id, { expiry_date: v });
                                      }
                                    } catch { toast.error("Kunne ikke oppdatere dato"); }
                                  }}
                                  className={`h-7 ${entry.expiry_date ? "rounded-l-md border-r-0" : "rounded-md"} border bg-white px-1.5 text-xs ${expired ? "border-red-300 text-red-600 font-semibold" : expiring ? "border-amber-300 text-amber-700" : "border-stone-200 text-stone-600"}`}
                                  data-testid={`expiry-${entry.id}`}
                                  title="Utløpsdato"
                                />
                                {entry.expiry_date && (
                                  <button
                                    onClick={async () => {
                                      patchEntry(item.id, entry.id, { expiry_date: "" });
                                      try {
                                        if (String(entry.id).startsWith("legacy-")) {
                                          await api.updatePantryItem(item.id, { expiry_date: "" });
                                        } else {
                                          await api.updatePantryLocationEntry(item.id, entry.id, { expiry_date: "" });
                                        }
                                      } catch { toast.error("Kunne ikke fjerne dato"); }
                                    }}
                                    className={`h-7 rounded-r-md border px-1.5 text-stone-400 hover:bg-red-50 hover:text-red-600 transition-colors ${expired ? "border-red-300" : expiring ? "border-amber-300" : "border-stone-200"}`}
                                    title="Fjern utløpsdato"
                                    data-testid={`clear-expiry-${entry.id}`}
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                )}
                              </div>

                              <div className="flex items-center gap-1 ml-auto flex-wrap">
                                <button
                                  onClick={() => toggleInStock(item, entry)}
                                  className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold transition-colors border ${outOfStock ? "bg-stone-100 text-stone-600 border-stone-200 hover:bg-stone-200" : "bg-white text-stone-600 border-stone-200 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200"}`}
                                  title={outOfStock ? `Marker som ${stockLabel}` : `Marker som tomt / ikke ${stockLabel}`}
                                  data-testid={`toggle-instock-${entry.id}`}
                                >
                                  {outOfStock ? <><PackageCheck className="h-3 w-3" /> {stockLabelTitle}</> : <><PackageX className="h-3 w-3" /> Tomt</>}
                                </button>
                                <button onClick={() => markAsWaste(item, entry)} className="p-1 hover:bg-red-50 rounded-md text-stone-400 hover:text-red-600 transition-colors" title="Marker som svinn" data-testid={`waste-entry-${entry.id}`}>
                                  <AlertTriangle className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => openWithdraw(item, entry)} className="p-1 hover:bg-orange-50 rounded-md text-stone-400 hover:text-orange-600 transition-colors" title="Ta ut" data-testid={`withdraw-entry-${entry.id}`}>
                                  <ArrowDownToLine className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => deleteLocationLine(item, entry)} className="p-1 hover:bg-red-50 rounded-md text-stone-400 hover:text-red-500 transition-colors" title="Fjern denne lagerplassen" data-testid={`delete-entry-${entry.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        {/* Add location row */}
                        <button
                          onClick={() => addLocationLine(item)}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-seawater-700 hover:bg-sailcloth-100 transition-colors border-t border-dashed border-stone-200"
                          data-testid={`add-location-line-${item.id}`}
                        >
                          <Plus className="h-3.5 w-3.5" /> Legg til lagerplass
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Item Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col p-0" data-testid="pantry-form-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-stone-100">
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>{editingId ? "Rediger vare" : "Legg til vare"}</DialogTitle>
            <DialogDescription>Registrer varer i beholdningen</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          <div className="space-y-4">
            <div className="relative">
              <input
                value={form.name}
                onChange={(e) => { setForm({ ...form, name: e.target.value }); setSuggestionsOpen(true); }}
                onFocus={() => setSuggestionsOpen(true)}
                onBlur={() => setTimeout(() => setSuggestionsOpen(false), 150)}
                placeholder="Varenavn *"
                className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none"
                data-testid="pantry-name-input"
                autoComplete="off"
              />
              {suggestionsOpen && nameSuggestions.length > 0 && (
                <div
                  className="absolute z-20 left-0 right-0 mt-1 bg-white border border-stone-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
                  data-testid="pantry-name-suggestions"
                >
                  <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-stone-400 font-semibold border-b border-stone-100">
                    Tidligere varer {suggestLoading && <span className="opacity-60">...</span>}
                  </p>
                  {nameSuggestions.map((s) => (
                    <button
                      type="button"
                      key={s.name}
                      onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-sailcloth-100 text-left border-b border-stone-50 last:border-0"
                      data-testid={`pantry-name-suggestion-${s.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span className="font-medium text-stone-800 truncate">{s.name}</span>
                      <span className="flex items-center gap-1.5 flex-shrink-0">
                        {s.category && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-600">
                            {CATEGORY_LABELS[s.category] || s.category}
                          </span>
                        )}
                        {s.count > 0 && (
                          <span className="text-[10px] text-stone-400">×{s.count}</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <input type="number" step="0.1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Mengde" className="rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="pantry-amount-input" />
              <Select value={form.unit || "stk"} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger data-testid="pantry-unit-input"><SelectValue placeholder="Enhet" /></SelectTrigger>
                <SelectContent>
                  {UNITS.map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="pantry-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1">Lagringsplass</label>
                <div className="grid grid-cols-2 gap-1.5" data-testid="pantry-location-picker">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, location_id: "" })}
                    className={`flex items-center gap-1 px-2 py-2 rounded-md border text-xs font-medium transition-all ${form.location_id === "" ? "bg-navy-800 text-white border-navy-800" : "bg-white text-stone-600 border-stone-200 hover:border-seawater-500"}`}
                    data-testid="loc-btn-auto"
                  >
                    ✨ Auto
                  </button>
                  {locations.map((loc) => {
                    const meta = TYPE_META[loc.type] || TYPE_META.fridge;
                    const Icon = meta.icon;
                    const selected = form.location_id === loc.id;
                    return (
                      <button
                        key={loc.id}
                        type="button"
                        onClick={() => setForm({ ...form, location_id: loc.id })}
                        className={`flex items-center gap-1 px-2 py-2 rounded-md border text-xs font-medium transition-all truncate ${selected ? "bg-navy-800 text-white border-navy-800" : `bg-white ${meta.color.split(' ')[1]} border-stone-200 hover:border-seawater-500`}`}
                        data-testid={`loc-btn-${loc.id}`}
                        title={loc.name}
                      >
                        <Icon className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{loc.name}</span>
                      </button>
                    );
                  })}
                </div>
                {form.location_id === "" && form.name.trim() && (
                  <p className="text-[10px] text-navy-800 mt-1 italic">
                    Auto: PantryBuddy velger riktig plass basert på varenavnet
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1">Kategori</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="pantry-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1">Utløpsdato (valgfritt)</label>
              <input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="pantry-expiry-input" />
            </div>
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Notat (valgfritt)" className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="pantry-note-input" />
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1.5">Bilde (valgfritt)</label>
              <ItemImagePicker
                value={form.image_url}
                onChange={(url) => setForm({ ...form, image_url: url })}
                prompt={form.name}
              />
            </div>
          </div>
          </div>
          <div className="border-t border-stone-100 px-6 py-3 bg-white">
            <button onClick={handleSave} className="w-full inline-flex items-center justify-center rounded-md bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors" data-testid="save-pantry-item-btn">
              {editingId ? "Oppdater" : "Legg til"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Locations Dialog */}
      <Dialog open={locDialogOpen} onOpenChange={setLocDialogOpen}>
        <DialogContent className="max-w-md max-h-[92vh] flex flex-col p-0" data-testid="locations-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-100 flex-shrink-0">
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Lagringsplasser</DialogTitle>
            <DialogDescription>Legg til flere kjøleskap, frysere eller tørrproviant</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 flex-1 overflow-y-auto px-5 py-4 min-h-0">
            {/* Stack on mobile (input/select/button hver sin rad) - flex-row på sm+ */}
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={newLoc.name} onChange={(e) => setNewLoc({ ...newLoc, name: e.target.value })} placeholder="Navn (f.eks. Kjøleskap garasje)" className="flex-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="new-location-name" onKeyDown={(e) => e.key === "Enter" && addLocation()} />
              <Select value={newLoc.type} onValueChange={(v) => setNewLoc({ ...newLoc, type: v })}>
                <SelectTrigger className="w-full sm:w-36" data-testid="new-location-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fridge">Kjøleskap</SelectItem>
                  <SelectItem value="freezer">Fryser</SelectItem>
                  <SelectItem value="dry">Tørrproviant</SelectItem>
                  <SelectItem value="cleaning">Renhold</SelectItem>
                  <SelectItem value="bathroom">Toalett & Bad</SelectItem>
                </SelectContent>
              </Select>
              <button onClick={addLocation} className="w-full sm:w-auto rounded-md bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors" data-testid="save-location-btn">Legg til</button>
            </div>
            <div className="space-y-1">
              {locations.map((loc) => {
                const meta = TYPE_META[loc.type] || TYPE_META.fridge;
                const Icon = meta.icon;
                const isEditing = editingLocId === loc.id;
                return (
                  <div key={loc.id} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${meta.color}`} data-testid={`location-item-${loc.id}`}>
                    <div className="flex items-center gap-2 flex-1">
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      {isEditing ? (
                        <input
                          autoFocus
                          defaultValue={loc.name}
                          onBlur={async (e) => {
                            const v = e.target.value.trim();
                            if (v && v !== loc.name) {
                              try {
                                await api.updateStorageLocation(loc.id, { name: v });
                                toast.success("Navn oppdatert");
                                loadData();
                              } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
                            }
                            setEditingLocId(null);
                          }}
                          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setEditingLocId(null); }}
                          className="flex-1 bg-white/90 rounded px-1.5 py-0.5 text-sm font-medium text-stone-800 outline-none focus:ring-2 focus:ring-seawater-500"
                          data-testid={`rename-input-${loc.id}`}
                        />
                      ) : (
                        <span className="text-sm font-medium">{loc.name}</span>
                      )}
                      <span className="text-xs opacity-60">{meta.label}</span>
                    </div>
                    <button onClick={() => setEditingLocId(loc.id)} className="p-1 hover:bg-white/50 rounded text-current opacity-40 hover:opacity-100 transition-all" title="Endre navn" data-testid={`rename-location-${loc.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => deleteLocation(loc.id)} className="p-1 hover:bg-white/50 rounded text-current opacity-40 hover:opacity-100 transition-all" data-testid={`delete-location-${loc.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Withdraw Dialog */}
      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent className="max-w-sm" data-testid="withdraw-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ta ut vare</DialogTitle>
            <DialogDescription>Registrer uttak fra beholdningen</DialogDescription>
          </DialogHeader>
          {withdrawItem && (
            <div className="space-y-4">
              <div className="p-3 bg-stone-50 rounded-lg">
                <span className="text-sm font-medium text-stone-800">{withdrawItem.name}</span>
                <span className="text-xs text-stone-400 ml-2">Tilgjengelig: {withdrawItem.amount} {withdrawItem.unit}</span>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1">Mengde å ta ut</label>
                <input value={withdrawForm.amount} onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })} placeholder={withdrawItem.amount || "Alt"} className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="withdraw-amount-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1">Grunn (valgfritt)</label>
                <input value={withdrawForm.reason} onChange={(e) => setWithdrawForm({ ...withdrawForm, reason: e.target.value })} placeholder="F.eks. Brukt til middag, Utgått..." className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="withdraw-reason-input" />
              </div>
              <button onClick={handleWithdraw} className="w-full inline-flex items-center justify-center rounded-md bg-orange-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-orange-800 transition-colors gap-2" data-testid="confirm-withdraw-btn">
                <ArrowDownToLine className="h-4 w-4" /> Bekreft uttak
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pantry trash (soft-delete recovery) */}
      <PantryTrashDialog
        open={trashOpen}
        onOpenChange={setTrashOpen}
        onChanged={loadData}
      />

      {/* Quick add previously-bought items */}
      <QuickAddPantryDialog
        open={quickAddOpen}
        onOpenChange={setQuickAddOpen}
        defaultLocationId={activeLocId !== "all" ? activeLocId : ""}
        onAdded={loadData}
      />

      {/* Delete-all PIN confirmation */}
      <DeleteAllPantryDialog
        open={deletePinOpen}
        onOpenChange={setDeletePinOpen}
        requiredPin={DELETE_ALL_PIN}
        onDeleted={loadData}
      />

      {/* Edit category-tab order & subgroups */}
      <EditCategoryGroupsDialog
        open={editGroupsOpen}
        onOpenChange={setEditGroupsOpen}
        boatId={activeBoat?.id}
        categories={activeCategoryList}
        labels={CATEGORY_LABELS}
        initialOrder={activeBoat?.category_order || []}
        initialSubgroups={activeBoat?.category_subgroups || {}}
        onSaved={loadActiveBoat}
      />

      {/* Barcode scanner — adds a new pantry item with product info from OpenFoodFacts */}
      <BarcodeScannerDialog
        open={barcodeOpen}
        onOpenChange={setBarcodeOpen}
        onDetected={handleBarcodeDetected}
      />

      {/* Unified cleanup wizard — shares state with auto-fix-images via shared hook */}
      <PantryCleanupDialog
        open={cleanupOpen}
        onOpenChange={setCleanupOpen}
        cleanup={cleanup}
      />

      {/* Auto-fix images (same cleanup hook, just narrower mode) */}
      <PantryCleanupDialog
        open={autoFixImagesOpen}
        onOpenChange={setAutoFixImagesOpen}
        mode="images"
        cleanup={cleanup}
      />

      {/* Floating progress pill — visible when a background cleanup is running
          but both dialogs are closed. Clicking it opens the dialog so the user
          can monitor progress or accept the review when ready. */}
      {(cleanup.state.phase === "names" || cleanup.state.phase === "images" || cleanup.state.phase === "duplicates" || cleanup.state.phase === "review") && !cleanupOpen && !autoFixImagesOpen && (
        <button
          onClick={() => setCleanupOpen(true)}
          className="fixed bottom-20 right-4 z-40 inline-flex items-center gap-2 rounded-full bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg hover:bg-violet-700 transition-colors animate-pulse"
          data-testid="cleanup-floating-pill"
          title="Opprydding pågår i bakgrunnen – trykk for å sjå framdrift"
        >
          <Sparkles className="h-4 w-4" />
          {cleanup.state.phase === "names" && "Forenkler navn…"}
          {cleanup.state.phase === "images" && (cleanup.state.progress.total > 0
            ? `Hentar bilete (${cleanup.state.progress.done}/${cleanup.state.progress.total})`
            : "Hentar bilete…")}
          {cleanup.state.phase === "duplicates" && "Finn duplikat…"}
          {cleanup.state.phase === "review" && `Klart – ${cleanup.state.imageSuggestions.length} bilete-forslag`}
        </button>
      )}

      {/* Quick image editor — replace or AI-regenerate the image for a single item */}
      <Dialog open={!!imageEditItem} onOpenChange={(o) => { if (!o) { setImageEditItem(null); setImageEditUrl(""); } }}>
        <DialogContent className="max-w-sm" data-testid="pantry-image-edit-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Bilde – {imageEditItem?.name}
            </DialogTitle>
            <DialogDescription>
              Last opp eit nytt foto, hent butikkbilde, eller la AI generere ein ny variant. Endringa lagrast med ein gong du trykker «Lagre».
            </DialogDescription>
          </DialogHeader>
          <ItemImagePicker
            value={imageEditUrl}
            onChange={setImageEditUrl}
            prompt={imageEditItem?.name || ""}
          />
          <div className="flex items-center justify-end gap-2 mt-3">
            <button
              onClick={() => { setImageEditItem(null); setImageEditUrl(""); }}
              className="px-3 py-2 text-sm font-medium text-stone-600 hover:text-stone-800"
              data-testid="pantry-image-edit-cancel"
            >
              Avbryt
            </button>
            <button
              onClick={saveImageEdit}
              disabled={imageEditSaving || imageEditUrl === (imageEditItem?.image_url || "")}
              className="rounded-md bg-navy-800 px-4 py-2 text-sm font-medium text-white hover:bg-navy-900 transition-colors disabled:opacity-60"
              data-testid="pantry-image-edit-save"
            >
              {imageEditSaving ? "Lagrer…" : "Lagre"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Standardize names dialog */}
      <Dialog open={standardizeOpen} onOpenChange={setStandardizeOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0" data-testid="standardize-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-stone-100">
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <Sparkles className="h-5 w-5 text-violet-600" />
              Standardiser varenavn
            </DialogTitle>
            <DialogDescription className="text-xs text-stone-500">
              Butikknavn (Coop, Rema, First Price …) fjernes fra varenavnet. Rediger forslagene direkte hvis du ser skrivefeil — det du skriver inn er det som lagres.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {standardizeLoading && standardizeChanges.length === 0 ? (
            <div className="py-10 text-center text-sm text-stone-500">Henter forslag …</div>
          ) : standardizeChanges.length === 0 ? (
            <div className="py-10 text-center text-sm text-stone-500" data-testid="standardize-no-changes">
              Ingen varenavn trenger oppdatering — du har allerede rene navn.
            </div>
          ) : (
            <div className="space-y-2" data-testid="standardize-changes-list">
              {standardizeChanges.map((c, idx) => (
                <div key={c.id} className="rounded-md border border-stone-200 bg-white p-2.5" data-testid={`standardize-change-${c.id}`}>
                  <div className="text-[11px] text-stone-400 line-through mb-1 truncate">{c.before}</div>
                  <div className="flex items-center gap-2">
                    <input
                      value={c.after || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setStandardizeChanges((prev) => prev.map((p, i) => i === idx ? { ...p, after: v } : p));
                      }}
                      className="flex-1 rounded-md border border-stone-200 bg-white px-2.5 py-1.5 text-sm font-medium text-stone-800 focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none"
                      data-testid={`standardize-edit-${c.id}`}
                      placeholder="Endre navnet om det er feilstavet…"
                    />
                    {c.brand && (
                      <span className="text-[10px] uppercase tracking-wider text-violet-600 flex-shrink-0">· {c.brand}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
          <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-stone-100 bg-white">
            <button
              onClick={() => setStandardizeOpen(false)}
              disabled={standardizeLoading}
              className="rounded-md border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-stone-600 hover:bg-stone-50"
              data-testid="standardize-cancel"
            >
              Avbryt
            </button>
            <button
              onClick={applyStandardize}
              disabled={standardizeLoading || standardizeChanges.length === 0}
              className="inline-flex items-center gap-1.5 rounded-md bg-navy-800 hover:bg-navy-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              data-testid="standardize-apply"
            >
              <Check className="h-3 w-3" /> {standardizeLoading ? "Lagrer..." : `Oppdater ${standardizeChanges.length}`}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Counting mode dialog */}
      <CountingMode
        open={countingOpen}
        onOpenChange={setCountingOpen}
        items={items}
        onChanged={loadData}
      />
    </div>
  );
}
